<template>
  <div
    class="select"
    :class="classes"
    v-if="authStore.hasFeature('ingest')"
  >
    <select
      v-model="fieldData.value"
      :disabled="fieldData.loading || !fieldDefinition.editable || undefined"
    >
      <option
        v-if="!fieldDefinition.required || !fieldDefinition.default"
        :disabled="fieldDefinition.required || undefined"
        :value="null"
      >
        &mdash;
      </option>
      <option
        v-for="bucket in ingestStore.buckets"
        :key="bucket.name"
        :value="bucket.name"
      >
        {{ bucket.name }}
      </option>
      <!--
        We may have a read-only configuration with an invalid bucket name.
        This would cause a <select> to be empty, which is not helpful for troubleshooting,
        so we add a dummy option with the invalid value only on a read-only field
      -->
      <option
        v-if="
          !fieldDefinition.editable &&
          typeof fieldData.value === 'string' &&
          !(fieldData.value in ingestStore.buckets)
        "
        :value="fieldData.value"
      >
        {{ fieldData.value }}
      </option>
    </select>
  </div>
  <input
    v-else
    class="input"
    type="text"
    v-model.trim="fieldData.value"
    :class="classes"
    placeholder="S3 bucket name"
    :disabled="fieldData.loading || !fieldDefinition.editable || undefined"
  />
  <p
    class="help is-danger"
    v-for="(error, i) in [...localErrors, ...fieldData.errors]"
    :key="i"
  >
    {{ error }}
  </p>
</template>

<script setup lang="ts">
import { isEmpty } from 'lodash'
import { computed } from 'vue'

import { errorParser } from '@/helpers'
import { useAuthStore, useIngestStore } from '@/stores'

import { type FieldModel, type FieldProps, ValidationError, defineValidator } from '.'

type Constraint = { type: 's3_bucket'; many: false }
const { fieldDefinition } = defineProps<FieldProps<Constraint>>()
const fieldData = defineModel<FieldModel<Constraint>>({ required: true })

const authStore = useAuthStore()
const ingestStore = useIngestStore()

const classes = computed(() => ({
  'is-danger': !fieldData.value.valid,
  'is-loading': fieldData.value.loading,
}))

const { localErrors } = defineValidator(
  fieldDefinition,
  fieldData.value,
  async (value: unknown) => {
    // Do not perform any validation when the S3 ingest feature is disabled and ListBuckets is unavailable
    if (!authStore.hasFeature('ingest')) return

    // Load buckets when necessary
    if (isEmpty(ingestStore.buckets)) {
      try {
        await ingestStore.listBuckets()
      } catch (err) {
        throw new ValidationError([`Could not list available buckets: ${errorParser(err)}`])
      }
    }

    if (value === null) return
    if (typeof value !== 'string') throw new ValidationError(['Bucket name should be a string.'])

    if (!(value in ingestStore.buckets)) throw new ValidationError(['This bucket does not exist.'])
  },
)
</script>
